export type InvoiceLocationStatus = 'Captured' | 'PermissionDenied' | 'PositionUnavailable' |
  'Timeout' | 'Unsupported' | 'InsecureContext' | 'InvalidData';

export interface InvoiceCreationLocation {
  captureStatus: InvoiceLocationStatus;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  capturedAtUtc?: string;
}

// One reading at invoice submission. No watching, reverse geocoding or local storage.
export const captureInvoiceLocation = (): Promise<InvoiceCreationLocation> => {
  if (!window.isSecureContext) return Promise.resolve({ captureStatus: 'InsecureContext' });
  if (!navigator.geolocation) return Promise.resolve({ captureStatus: 'Unsupported' });

  return new Promise(resolve => {
    let settled = false;
    const finish = (reading: InvoiceCreationLocation) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(watchdog);
      resolve(reading);
    };
    // Browser timeout may exclude time spent waiting for a permission answer.
    const watchdog = window.setTimeout(() => finish({ captureStatus: 'Timeout' }), 12000);
    try {
      navigator.geolocation.getCurrentPosition(position => {
        const { latitude, longitude, accuracy } = position.coords;
        const timestamp = position.timestamp;
        if (!Number.isFinite(latitude) || Math.abs(latitude) > 90 ||
            !Number.isFinite(longitude) || Math.abs(longitude) > 180 ||
            !Number.isFinite(accuracy) || accuracy < 0 || accuracy > 999999999 ||
            !Number.isFinite(timestamp) || Math.abs(Date.now() - timestamp) > 5 * 60000) {
          finish({ captureStatus: 'InvalidData' });
          return;
        }
        finish({ captureStatus: 'Captured', latitude, longitude, accuracyMeters: accuracy,
          capturedAtUtc: new Date(timestamp).toISOString() });
      }, error => finish({ captureStatus: error.code === 1 ? 'PermissionDenied' :
        error.code === 3 ? 'Timeout' : 'PositionUnavailable' }),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 });
    } catch {
      finish({ captureStatus: 'PositionUnavailable' });
    }
  });
};
